import { nameOfSide } from '@polf/shared/match'

import {
  type CurrentPlayerFlightMatchesQuery,
  type HoleScoreFragment,
  MatchSideEnum,
} from '../../../__generated__/hooks'
import MatchHoleWinner from '../../../components/MatchHoleWinner'
import { decidedMargin, runningStanding } from '../../../util/match-progression'

type Match =
  CurrentPlayerFlightMatchesQuery['currentPlayerFlightMatches'][number]

/**
 * How the match evolved, hole by hole.
 *
 * `state.holes` is the per-hole outcome in PLAY order, and it stops at the
 * deciding hole — so the running standing derived from it stops there too.
 * Holes after that were played for the stroke card and are shown blank rather
 * than carrying a standing they had no part in.
 */
const MatchView = ({
  matches,
  holes,
}: {
  matches: Match[]
  holes: HoleScoreFragment[]
}) => (
  <div className="flex flex-col gap-4 px-2 pb-6">
    {matches.map((match) => {
      const { state } = match
      const standings = runningStanding(state?.holeSwings ?? [])
      const leaderName = nameOfSide(match, state?.leader)

      return (
        <section
          key={match.id}
          className="bg-cream-card rounded-lg p-2"
          aria-label={`Match ${match.matchNumber}`}
        >
          <header className="flex items-baseline justify-between px-2 py-1">
            <span className="text-xs font-bold uppercase tracking-caps text-ink-700 truncate">
              {nameOfSide(match, MatchSideEnum.A)} mot{' '}
              {nameOfSide(match, MatchSideEnum.B)}
            </span>
            <span className="text-xs font-mono num font-bold text-ink-900 shrink-0">
              {state?.margin
                ? `${leaderName ?? ''} ${decidedMargin(state)}`.trim()
                : standings[standings.length - 1] ?? 'AS'}
            </span>
          </header>

          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-caps text-ink-500">
                <th className="text-left font-bold py-1 px-2">Hull</th>
                <th className="text-center font-bold py-1">Vinner</th>
                <th className="text-right font-bold py-1 px-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {holes.map((h, i) => {
                const legs = state?.holeLegs?.[i]
                const played = legs != null

                return (
                  <tr
                    key={h.hole.number}
                    className="border-t border-ink-100/60"
                  >
                    <td className="py-1.5 px-2 font-mono num text-ink-700">
                      {h.hole.number}
                    </td>
                    <td className="py-1.5 text-center text-ink-900">
                      {!played ? (
                        <span className="text-ink-300">–</span>
                      ) : (
                        <MatchHoleWinner
                          legs={legs}
                          nameFor={(side) => nameOfSide(match, side)}
                        />
                      )}
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono num font-bold text-ink-900">
                      {played ? standings[i] : ''}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </section>
      )
    })}
  </div>
)

export default MatchView
